import { useEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useRenderPreview } from '@/api/rpc/hooks/communications/useEmailTemplates';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { parseEmailSpec, parseJsonRecord } from './emailTemplateJsonSpec';

interface JsonTemplateEditorProps {
  jsonSpec: string;
  onJsonSpecChange: (value: string) => void;
  sampleVariables: string;
  onSampleVariablesChange: (value: string) => void;
  expanded?: boolean;
}

export const DEFAULT_SAMPLE_VARIABLES = `{
  "companyName": "Northwind Supply Co.",
  "customerName": "Acme Produce",
  "invoiceNumber": "INV-1001",
  "issuedDate": "May 1, 2026",
  "dueDate": "May 15, 2026",
  "amountDue": "$1,250.00",
  "paymentUrl": "https://example.com/pay/INV-1001",
  "billingEmail": "billing@northwind.example",
  "isPastDue": false,
  "lineItems": [
    {
      "sku": "CASE-APPLES",
      "description": "Organic apples, 40 lb cases",
      "quantity": 12,
      "total": "$720.00"
    },
    {
      "sku": "CASE-PEARS",
      "description": "Green pears, 40 lb cases",
      "quantity": 8,
      "total": "$480.00"
    },
    {
      "sku": "FREIGHT",
      "description": "Freight surcharge",
      "quantity": 1,
      "total": "$50.00"
    }
  ]
}`;

export function JsonTemplateEditor({
  jsonSpec,
  onJsonSpecChange,
  sampleVariables,
  onSampleVariablesChange,
  expanded = false,
}: JsonTemplateEditorProps) {
  const gt = useGT();
  const renderPreview = useRenderPreview();
  const { isPending, mutate } = renderPreview;
  const requestIdRef = useRef(0);
  const [isVariablesOpen, setIsVariablesOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const timeout = window.setTimeout(() => {
      const specResult = parseEmailSpec(jsonSpec);
      if (specResult.error || !specResult.data) {
        setPreviewHtml('');
        setPreviewError(specResult.error ?? 'Invalid JSON.');
        return;
      }

      const variablesResult = parseJsonRecord(sampleVariables);
      if (variablesResult.error || !variablesResult.data) {
        setPreviewHtml('');
        setPreviewError(
          gt('Sample variables JSON is invalid: {message}', {
            context: 'json render email template preview validation error',
            message: variablesResult.error ?? 'Invalid JSON.',
          }),
        );
        return;
      }

      const previewInput = {
        jsonSpec: specResult.data,
        variables: variablesResult.data,
      };
      mutate(previewInput, {
        onSuccess: (result) => {
          if (requestIdRef.current !== requestId) return;
          setPreviewHtml(result.html);
          setPreviewError('');
        },
        onError: (error) => {
          if (requestIdRef.current !== requestId) return;
          setPreviewHtml('');
          setPreviewError(error.message);
        },
      });
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [gt, jsonSpec, mutate, sampleVariables]);

  return (
    // Expanded (full-page editor): join the page's flex column so the split
    // row below can grow to the bottom of the settings content pane.
    <div
      className={cn(
        expanded ? 'flex min-h-0 flex-1 flex-col gap-3' : 'space-y-3',
      )}
    >
      <Collapsible open={isVariablesOpen} onOpenChange={setIsVariablesOpen}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1 px-1"
          >
            {isVariablesOpen ? (
              <CaretDownIcon className="h-3.5 w-3.5" />
            ) : (
              <CaretRightIcon className="h-3.5 w-3.5" />
            )}
            <T context="json render email template sample data section label">
              Sample Variables
            </T>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2">
          <Label htmlFor="jsonSampleVariables" className="text-xs">
            <T context="json render email template sample data textarea label">
              Preview data passed as $state
            </T>
          </Label>
          <Textarea
            id="jsonSampleVariables"
            value={sampleVariables}
            onChange={(event) => onSampleVariablesChange(event.target.value)}
            className="min-h-28 font-mono text-xs"
            spellCheck={false}
          />
        </CollapsibleContent>
      </Collapsible>

      <ResizablePanelGroup
        direction="horizontal"
        className={cn(
          'rounded-md border',
          // Expanded (full-page editor): grow to the bottom of the settings
          // content pane (the ancestors thread flex-col + min-h-0 down from
          // the page root). The floor keeps the panels usable on short
          // viewports — the pane scrolls instead. The inner panels fill this
          // container via h-full/flex-1.
          expanded ? 'min-h-[320px] flex-1' : 'min-h-[480px]',
        )}
      >
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="flex h-full min-h-0 flex-col">
            <div className="border-b px-3 py-2 text-sm font-medium">
              <T context="json render email template editor panel title">
                Template Blocks
              </T>
            </div>
            <Textarea
              value={jsonSpec}
              onChange={(event) => onJsonSpecChange(event.target.value)}
              placeholder={gt('Paste a template blocks email spec here', {
                context: 'json render email template editor placeholder',
              })}
              className={cn(
                'h-full flex-1 resize-none rounded-none border-0 font-mono text-xs focus-visible:ring-0',
                expanded ? 'min-h-0' : 'min-h-[430px]',
              )}
              spellCheck={false}
            />
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="flex h-full min-h-0 flex-col bg-muted/20">
            <div className="border-b bg-background px-3 py-2 text-sm font-medium">
              <T context="json render email template preview panel title">
                Live Preview
              </T>
            </div>
            <div className="relative min-h-0 flex-1 bg-background">
              {isPending && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
                  <LoadingSpinner />
                </div>
              )}
              {previewError ? (
                <div className="p-3 text-sm text-destructive">
                  {previewError}
                </div>
              ) : previewHtml ? (
                <iframe
                  title={gt('Email template preview', {
                    context: 'json render email template preview iframe title',
                  })}
                  srcDoc={previewHtml}
                  sandbox=""
                  className={cn(
                    'h-full w-full border-0 bg-white',
                    expanded ? 'min-h-0' : 'min-h-[430px]',
                  )}
                />
              ) : (
                <div
                  className={cn(
                    'flex h-full items-center justify-center p-4 text-sm text-muted-foreground',
                    expanded ? 'min-h-0' : 'min-h-[430px]',
                  )}
                >
                  <T context="json render email template empty preview message">
                    Enter a valid template blocks spec to render a preview.
                  </T>
                </div>
              )}
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
